import * as React from "react";
import type { SeriesDef } from "@/components/ui/chart";
import { outcomeColor, OutcomeTag } from "@/components/ui/outcome";
import { groupTerm, OUTCOME_GROUPS, outcomeGroup, recordedOutcome, type OutcomeGroup } from "@/lib/dispositions";
import { humanize } from "@/lib/lead-card";
import { useCallDispositions, useOutcomeHistory } from "@/lib/queries";

export interface OutcomeLook {
  label: string;
  /** Null for a slug the catalog does not hold: shown without a colour. */
  group: OutcomeGroup | null;
}

/**
 * How a recorded outcome's slug reads on a screen (ACL-508): its label and its group's colour, both
 * from one list, a campaign's (`campaignId`) or the tenant's whole catalog (null), which every
 * campaign picks from. A slug the list does not hold is its own words, with no colour.
 */
export function useOutcomeLook(campaignId: string | null = null, enabled = true): (slug: string) => OutcomeLook {
  const catalog = useCallDispositions(campaignId, enabled);
  // A switched-off outcome on an old record still reads by its label (ACL-574).
  const history = useOutcomeHistory(enabled);
  return React.useCallback(
    (slug: string) => {
      const found = recordedOutcome(slug, catalog.data, history.data);
      return { label: found?.label ?? humanize(slug) ?? slug, group: found ? outcomeGroup(found) : null };
    },
    [catalog.data, history.data],
  );
}

/** A recorded outcome as its tag: its words in its group's colour. */
export function RecordedOutcome({
  slug, look, size, className,
}: {
  slug: string;
  look: (slug: string) => OutcomeLook;
  size?: "sm" | "md";
  className?: string;
}) {
  const { label, group } = look(slug);
  return (
    <OutcomeTag group={group} size={size} className={className} title={label}>
      {label}
    </OutcomeTag>
  );
}

/** The chart series of the outcome groups among `groups`, in the window's order and each in its
 *  own colour, so a report's bars are coloured as the outcomes are everywhere else. A slug no
 *  group was found for counts under "No group". */
export function groupSeries(groups: Iterable<OutcomeGroup | null>): SeriesDef[] {
  const present = new Set(groups);
  const out: SeriesDef[] = OUTCOME_GROUPS.filter((g) => present.has(g)).map((g) => ({
    key: g,
    label: groupTerm(g).label,
    slot: "other",
    color: outcomeColor(g),
  }));
  if (present.has(null)) out.push({ key: NO_GROUP, label: "No group", slot: "other" });
  return out;
}

/** The series key of the outcomes `groupSeries` found no group for. */
export const NO_GROUP = "none";
